import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { connect, useDispatch } from 'react-redux';

import { ticketParticipantsSet, ticketStagesSet } from '~/actions/ticket';
import {
  reassignCoordinator,
  resetApproval,
  resetApprovals,
} from '~/api/tickets';
import UserName from '~/components/Client/UserName';
import AccessControl from '~/components/Shared/AccessControl/AccessControl';
import { showToast } from '~/components/Shared/EcToast/showToast';
import UserSelect from '~/components/Shared/UserSelect/UserSelect';
import { ActionsMenu } from '~/eds/src/components/ActionsMenu/ActionsMenu';
import { FormField } from '~/eds/src/components/FormField/FormField';
import { useToast } from '~/eds/src/components/hooks/useToast';
import { TextArea } from '~/eds/src/components/TextArea/TextArea';
import { TicketPermissionType } from '~/enums/TicketPermissionType';
import { TicketStageType } from '~/enums/TicketStageType';
import { TicketStatusType } from '~/enums/TicketStatusType';
import { useReassignSignerModal } from '~/features/ticket/stages/sign/recipients/useReassignSignerModal';
import { EsignatureSignerStatusValue } from '~/features/ticket/types/esignature';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { withUsers } from '~/hocs/withUsers';
import { useAsync } from '~/hooks/useAsync';
import { getJudgmentById } from '~/reducers/ticketReviewer';
import { api } from '~/redux/api/createApi';
import * as TAGS from '~/redux/api/endpoints/allEndpoints';
import { TagType } from '~/redux/api/TagType';
import { useRouting } from '~/routing/useRouting';
import * as toastTypes from '~/types/toast.types';
import Box from '~/ui/components/Box/Box';
import FlexLayout from '~/ui/components/FlexLayout/FlexLayout';
import Icon from '~/ui/components/Icon/Icon';
import Link from '~/ui/components/Link/Link';
import LoadingSpinner from '~/ui/components/LoadingSpinner/LoadingSpinner';
import Text from '~/ui/components/Text/Text';
import { useModal } from '~/ui/hooks/modal';
import { externalUserToUser } from '~/utils/user';

import { useActivityLogUpdater } from '../../shared/useActivityLogUpdater';
import { UserInfo } from './UserInfo';

const getAssigneeRole = (stage, intl) => {
  switch (stage) {
    case TicketStageType.Review:
      return intl.formatMessage({
        id: 'TicketUserItem.reviewerRole',
        defaultMessage: 'reviewer',
        description: 'Role name for reviewer',
      });
    case TicketStageType.Sign:
      return intl.formatMessage({
        id: 'TicketUserItem.signerRole',
        defaultMessage: 'signer',
        description: 'Role name for signer',
      });
    case TicketStageType.Finalize:
      return intl.formatMessage({
        id: 'TicketUserItem.finalizerRole',
        defaultMessage: 'finalizer',
        description: 'Role name for finalizer',
      });
    default:
      return null;
  }
};

function TicketUserItem({
  userId,
  externalUser,
  ticketId,
  userPermissions,
  children,
  isListItem,
  currentUserId,
  stageId,
  issueId,
  entityType,
  note,
  status,
  allItemsNeutral,
  disableAdditionalActions,
  isCounterpartySigner,
  // injected
  users,
  // connected
  ticketParticipantsSet,
  ticket,
  judgment,
  assigneeIds,
  assignAction,
  setAssignAction,
  isCoordinator,
}) {
  const intl = useIntl();
  const [reassign, setReassign] = useState(false);
  const [assigneeId, setAssigneeId] = useState(userId);
  const [comment, setComment] = useState('');
  const isAssignSelf = !assigneeIds.includes(currentUserId);
  const dispatch = useDispatch();
  const { navigate } = useRouting();
  const isEsignatureV2RevampEnabled = useFlag(FlagType.EsignatureV2Revamp);

  const assigneeRole = getAssigneeRole(ticket.stage, intl);
  const successToastMessage = assigneeRole
    ? intl.formatMessage(
        {
          id: 'TicketUserItem.newAssigneeSuccess',
          defaultMessage: 'New {role} assigned.',
          description:
            'Success message when new assignee is assigned. role example: "reviewer", "signer", "finalizer"',
        },
        { role: assigneeRole },
      )
    : intl.formatMessage({
        id: 'TicketUserItem.taskAssignedSuccess',
        defaultMessage: 'The task has been assigned.',
        description: 'Success message when task is assigned',
      });

  const updateActivityLog = useActivityLogUpdater({ ticketId });

  const [getTicketCurrentStage] =
    api.endpoints.getTicketCurrentStage.useLazyQuery();

  const [getTicketParticipants] =
    api.endpoints.getTicketParticipants.useLazyQuery();
  const { data: envelope } = api.endpoints.getEnvelope.useQuery(
    {
      ticketId,
    },
    {
      skip: !isEsignatureV2RevampEnabled,
    },
  );
  const { data: recipients = { signers: [] } } =
    api.endpoints.getRecipients.useQuery(
      {
        ticketId,
      },
      {
        skip:
          !isEsignatureV2RevampEnabled || ticket.stage !== TicketStageType.Sign,
      },
    );

  const updateTicketData = async () => {
    const stages = await getTicketCurrentStage({ ticketId }).unwrap();
    ticketStagesSet(stages);
    dispatch(api.util.invalidateTags([TAGS.ACL_TAG]));
    await updateActivityLog();
    const participants = await getTicketParticipants({ ticketId }).unwrap();
    ticketParticipantsSet(participants.data);
  };

  const [reassignApproval, { isLoading: isReassignApprovalLoading }] =
    api.endpoints.reassignApproval.useMutation();
  const { toast } = useToast();

  const handleReassignment = async (id) => {
    try {
      await reassignApproval({
        ticketId,
        issueId,
        oldAssigneeId: userId,
        newAssigneeId: id ?? assigneeId,
        entityType,
      }).unwrap();
      await updateTicketData();
      toast({ message: successToastMessage, status: 'success' });
    } catch (error) {
      const errorMessage = error?.response?.data?.data;
      const errorStatus = error?.response?.status;
      if (errorStatus === 403) {
        toast({
          message: intl.formatMessage({
            id: 'TicketUserItem.noPermissionError',
            defaultMessage: "You don't have permission to see this ticket.",
            description:
              'Error message when user lacks permission to view ticket',
          }),
          status: 'danger',
        });
        navigate('/workflow/tickets');
      }
      if (errorStatus === 400 && errorMessage?.includes('already assigned')) {
        toast({
          message: intl.formatMessage({
            id: 'TicketUserItem.alreadyAssignedError',
            defaultMessage:
              'Task already assigned to this person. Please select a different assignee.',
            description:
              'Error message when trying to assign task to someone who already has it',
          }),
          status: 'danger',
        });
      } else {
        toast({
          message: intl.formatMessage(
            {
              id: 'TicketUserItem.reassignmentFailedError',
              defaultMessage:
                'Reassignment failed. Please refresh the page and try again. ({errorMessage})',
              description:
                'Error message when reassignment fails. errorMessage is the specific error details',
            },
            { errorMessage },
          ),
          status: 'danger',
        });
      }
    }
  };

  const {
    executor: handleCoordinatorReassignment,
    isLoading: isReassignCoordinatorLoading,
  } = useAsync(
    reassignCoordinator,
    { ticketId, stageId, coordinatorId: assigneeId },
    {
      errorToastMessage: intl.formatMessage({
        id: 'TicketUserItem.coordinatorReassignmentError',
        defaultMessage:
          'Reassignment failed. Please refresh the page and try again.',
        description: 'Error message when coordinator reassignment fails',
      }),
      successHandler: async () => {
        try {
          await updateTicketData();
        } catch (error) {
          const errorStatus = error?.response?.status;
          if (errorStatus === 403) {
            toast({
              message: intl.formatMessage({
                id: 'TicketUserItem.noPermissionErrorAfterReassign',
                defaultMessage: "You don't have permission to see this ticket.",
                description:
                  'Error message when user lacks permission after reassignment',
              }),
              status: 'danger',
            });
            navigate('/workflow/tickets');
          }
        }
      },
      successToastMessage: intl.formatMessage({
        id: 'TicketUserItem.coordinatorAssignedSuccess',
        defaultMessage: 'New coordinator assigned.',
        description: 'Success message when new coordinator is assigned',
      }),
    },
  );

  const handleOnReassignClick = () => setReassign(true);

  const handleOnCancelReassignClick = () => {
    if (isListItem) {
      // if user clicks cancel, hide the drop down menu
      setAssignAction(false);
    }
    setReassign(false);
    setAssigneeId(null);
  };

  const handleAssign = (assigneeId) => {
    if (isListItem) {
      handleReassignment(assigneeId);
      setAssignAction(false);
    } else {
      handleCoordinatorReassignment();
    }
    setReassign(false);
  };

  const handleAssignClick = (isSelfClick) => {
    let newAssigneeId = null;
    if (isAssignSelf && !reassign) {
      if (isSelfClick) {
        newAssigneeId = currentUserId;
      } else {
        newAssigneeId = assigneeId;
      }
      setAssigneeId(newAssigneeId);
    }
    if (ticket.stage === TicketStageType.Sign && isListItem) {
      showReAssignSignerModal();
    } else {
      handleAssign(newAssigneeId);
    }
  };

  const onHide = () => {
    setComment('');
  };

  const itemActions =
    reassign || assignAction ? (
      <FlexLayout alignItems="center" space={6}>
        <UserSelect
          notAvailableUsers={assigneeIds}
          value={assigneeId}
          onChange={(assigneeId) => setAssigneeId(assigneeId)}
        />
        <FlexLayout
          alignItems="center"
          space={1}
          onClick={() => handleAssignClick(false)}
        >
          <Icon icon="checkmark" size="s" color="blue-500" />{' '}
          <Link variant="xs-dense-medium">
            {intl.formatMessage({
              id: 'TicketUserItem.assignLink',
              defaultMessage: 'Assign',
              description: 'Link text for assign action',
            })}
          </Link>
        </FlexLayout>
        <FlexLayout
          alignItems="center"
          space={1}
          onClick={handleOnCancelReassignClick}
        >
          <Link variant="xs-dense-medium">
            {intl.formatMessage({
              id: 'TicketUserItem.cancelLink',
              defaultMessage: 'Cancel',
              description: 'Link text for cancel action',
            })}
          </Link>
        </FlexLayout>
      </FlexLayout>
    ) : (
      children
    );

  const resetStageText = (ticketStage) => {
    switch (ticketStage) {
      case TicketStageType.Review:
        return intl.formatMessage({
          id: 'TicketUserItem.itemStageText',
          defaultMessage: 'Item',
          description: 'Stage text for review items',
        });
      case TicketStageType.Sign:
        return intl.formatMessage({
          id: 'TicketUserItem.signatureStageText',
          defaultMessage: 'Signature',
          description: 'Stage text for signatures',
        });
      case TicketStageType.Finalize:
        return intl.formatMessage({
          id: 'TicketUserItem.taskStageText',
          defaultMessage: 'Task',
          description: 'Stage text for tasks',
        });
      default:
        return '';
    }
  };
  const stageText = resetStageText(ticket.stage);
  const stageTextWithCondition = isListItem
    ? stageText
    : intl.formatMessage(
        {
          id: 'TicketUserItem.phasesText',
          defaultMessage: '{count, plural, =1 {Phase} other {Phases}}',
          description: 'Pluralized text for phases',
        },
        { count: 2 },
      ); // Using count > 1 for plural form

  const hasSignatures = ticket.document.hasSignatures;

  const [resetApprovalsModal, showResetApprovalsModal] = useModal({
    title: intl.formatMessage(
      {
        id: 'TicketUserItem.resetModalTitle',
        defaultMessage: 'Reset {stageText}',
        description:
          'Modal title for resetting stages. stageText example: "Items", "Signatures", "Tasks"',
      },
      { stageText: stageTextWithCondition },
    ),
    onHide,
    actionButton: {
      text: intl.formatMessage(
        {
          id: 'TicketUserItem.resetModalButton',
          defaultMessage: 'Reset {stageText}',
          description:
            'Modal button text for resetting. stageText example: "Items", "Signatures", "Tasks"',
        },
        { stageText: stageTextWithCondition },
      ),
      errorHandler: () => {
        showToast(
          toastTypes.ERROR,
          intl.formatMessage(
            {
              id: 'TicketUserItem.resetError',
              defaultMessage:
                'Unable to reset the {stageText}. Please try again.',
              description:
                'Error message when reset fails. stageText example: "item(s)", "signature(s)", "task(s)"',
            },
            { stageText: stageText.toLowerCase() + '(s)' },
          ),
        );
      },
      promise: async () => {
        isListItem
          ? await resetApproval(ticketId, issueId, comment, entityType, [
              userId,
            ])
          : await resetApprovals(ticketId, stageId, comment);
        await updateTicketData();
        if (isEsignatureV2RevampEnabled) {
          dispatch(
            api.util.invalidateTags([
              {
                type: TagType.TicketRecipients,
                id: ticketId,
              },
              {
                type: TagType.EsignatureEnvelope,
                id: ticketId,
              },
            ]),
          );
        }
        showToast(
          toastTypes.SUCCESS,
          intl.formatMessage(
            {
              id: 'TicketUserItem.resetSuccess',
              defaultMessage: '{stageText} successfully reset.',
              description:
                'Success message when reset completes. stageText example: "Item(s)", "Signature(s)", "Task(s)"',
            },
            { stageText: stageText + '(s)' },
          ),
        );
      },
    },
    content: (
      <>
        <Box p="5px 0 20px">
          {isListItem ? (
            <Text variant="s-spaced">
              <FormattedMessage
                id="TicketUserItem.resetItemConfirmation"
                defaultMessage="Are you sure you want to reset the item for {userName} as part of the {judgmentName} item? "
                description="Confirmation message for resetting individual item. userName and judgmentName are user and judgment names"
                values={{
                  userName: <UserName userId={userId} />,
                  judgmentName: judgment ? judgment.name : '',
                }}
              />
            </Text>
          ) : (
            <Text variant="s-spaced">
              <FormattedMessage
                id="TicketUserItem.resetAllConfirmation"
                defaultMessage="Are you sure you want to reset all of the {stageText} for this ticket?"
                description="Confirmation message for resetting all stages. stageText example: 'phases', 'signatures', 'tasks'"
                values={{
                  stageText: intl.formatMessage(
                    {
                      id: 'TicketUserItem.allStagesText',
                      defaultMessage:
                        '{count, plural, =1 {phase} other {phases}}',
                      description: 'Pluralized text for all stages being reset',
                    },
                    { count: 2 },
                  ),
                }}
              />
            </Text>
          )}
          {ticket.stage === TicketStageType.Sign && hasSignatures && (
            <Text variant="s-spaced-bold">
              {intl.formatMessage({
                id: 'TicketUserItem.signatureUploadWarning',
                defaultMessage:
                  'Signatures that were signed via upload will not be reset.',
                description:
                  'Warning that uploaded signatures will not be reset',
              })}
            </Text>
          )}
          <Text variant="s-spaced-bold">
            {intl.formatMessage({
              id: 'TicketUserItem.undoWarning',
              defaultMessage: "You won't be able to undo this action.",
              description: 'Warning that action cannot be undone',
            })}
          </Text>
        </Box>
        <FormField
          input={TextArea}
          value={comment}
          inputProps={{
            placeholder: intl.formatMessage({
              id: 'TicketUserItem.commentPlaceholder',
              defaultMessage: 'Add an optional comment…',
              description: 'Placeholder text for comment textarea',
            }),
            onChange: (value) => setComment(value),
          }}
          label={intl.formatMessage({
            id: 'TicketUserItem.commentLabel',
            defaultMessage: 'Comment',
            description: 'Label for comment field',
          })}
        />
      </>
    ),
  });

  const createResetApprovalTaskText = () => {
    switch (ticket.stage) {
      case TicketStageType.Finalize:
        return intl.formatMessage({
          id: 'TicketUserItem.resetTaskAction',
          defaultMessage: 'Reset Task',
          description: 'Action text for resetting task',
        });
      case TicketStageType.Review:
        return intl.formatMessage({
          id: 'TicketUserItem.resetItemAction',
          defaultMessage: 'Reset Item',
          description: 'Action text for resetting item',
        });
      case TicketStageType.Sign:
        return intl.formatMessage({
          id: 'TicketUserItem.resetSignatureAction',
          defaultMessage: 'Reset Signature',
          description: 'Action text for resetting signature',
        });
      default:
        return null;
    }
  };

  const createResetApprovalsTasksText = () => {
    switch (ticket.stage) {
      case TicketStageType.Finalize:
        return intl.formatMessage({
          id: 'TicketUserItem.resetAllTasksAction',
          defaultMessage: 'Reset All Tasks',
          description: 'Action text for resetting all tasks',
        });
      case TicketStageType.Sign:
        return intl.formatMessage({
          id: 'TicketUserItem.resetAllSignaturesAction',
          defaultMessage: 'Reset All Signatures',
          description: 'Action text for resetting all signatures',
        });
      default:
        return intl.formatMessage({
          id: 'TicketUserItem.resetAllItemsAction',
          defaultMessage: 'Reset All Items',
          description: 'Action text for resetting all items',
        });
    }
  };

  const shouldDisableResetAllSignatures = () => {
    if (allItemsNeutral) return true;
    if (ticket.stage !== TicketStageType.Sign) return false;

    const phases = ticket.stages.sign.phaseIds.map((id) => ticket.phases[id]);
    return phases.every((phase) => phase.status === TicketStatusType.Signed);
  };

  const shouldDisableResetAllSignaturesEsignRevamp = () => {
    if (ticket.stage !== TicketStageType.Sign) return false;

    const signers = recipients.signers;
    const allRecipientsNeutral = signers.every(
      (signer) =>
        signer.status?.value ===
        EsignatureSignerStatusValue.SignatureRequestNotSent,
    );
    const allRecipientsSigned = signers.every(
      (signer) => signer.status?.value === EsignatureSignerStatusValue.Signed,
    );
    return allRecipientsNeutral || allRecipientsSigned;
  };

  const isResetAllDisabled = isEsignatureV2RevampEnabled
    ? shouldDisableResetAllSignaturesEsignRevamp()
    : shouldDisableResetAllSignatures();
  const isLoading = isReassignApprovalLoading || isReassignCoordinatorLoading;
  const {
    reassignSignerModal: reAssignSignerModal,
    showReassignSignerModal: showReAssignSignerModal,
  } = useReassignSignerModal({
    handleAssign,
  });
  const menuName = `workflow-ticket-user-item-actions-menu-issue-${issueId}`;

  const getMenuItems = () => {
    const items = [];
    const isSignStageRevamped =
      ticket.stage === TicketStageType.Sign && isEsignatureV2RevampEnabled;
    const blockReassignmentOfSigners = isSignStageRevamped && Boolean(envelope);

    const assignedUser = users[userId];
    if ((assignedUser && !blockReassignmentOfSigners) || isCoordinator) {
      items.push({
        label: intl.formatMessage({
          id: 'TicketUserItem.reassignAction',
          defaultMessage: 'Re-assign',
          description: 'Menu action for reassigning',
        }),
        value: 're-assign',
        disabled: status === TicketStatusType.Done,
        onClick: () => handleOnReassignClick(),
      });
    }

    if (isCoordinator || !blockReassignmentOfSigners) {
      items.push({
        label: intl.formatMessage({
          id: 'TicketUserItem.assignToMeAction',
          defaultMessage: 'Assign to me',
          description: 'Menu action for assigning to current user',
        }),
        value: 'assign-to-me',
        disabled: currentUserId === userId || status === TicketStatusType.Done,
        onClick: () => handleAssignClick(true),
      });
    }

    if (
      isListItem &&
      status !== TicketStatusType.Signed &&
      !isSignStageRevamped
    ) {
      items.push({
        label: createResetApprovalTaskText(),
        value: 'reset-task',
        disabled: status === TicketStatusType.Pending,
        onClick: () => showResetApprovalsModal(),
      });
    } else if (!isSignStageRevamped || (isSignStageRevamped && isCoordinator)) {
      items.push({
        label: createResetApprovalsTasksText(),
        value: 'reset-all',
        disabled: isResetAllDisabled,
        onClick: () => showResetApprovalsModal(),
      });
    }

    return items;
  };

  return (
    <>
      <FlexLayout
        alignItems="center"
        disabled={isLoading}
        justifyContent="space-between"
        p={4}
        sx={{ border: isListItem ? undefined : 'border', borderRadius: 'm' }}
      >
        <UserInfo
          isCounterpartySigner={isCounterpartySigner}
          userId={userId}
          externalUser={
            externalUser ? externalUserToUser(externalUser) : undefined
          }
          currentStage={ticket.stage}
          isCoordinator={isCoordinator}
        />

        {isLoading && <LoadingSpinner size="s" />}
        <FlexLayout alignItems="center" space={6}>
          {itemActions}
          <AccessControl
            userPermissions={userPermissions}
            requiredPermission={TicketPermissionType.ReassignAny}
          >
            {!isCounterpartySigner &&
              !disableAdditionalActions &&
              !reassign &&
              !assignAction &&
              getMenuItems().length > 0 && (
                <ActionsMenu name={menuName} actions={getMenuItems()} />
              )}
          </AccessControl>
        </FlexLayout>
      </FlexLayout>
      {note}
      {resetApprovalsModal}
      {reAssignSignerModal}
    </>
  );
}

const mapStateToProps = ({ ticket }, { issueId }) => {
  const judgment = getJudgmentById(ticket, issueId);

  let assigneeIds = [];
  if (judgment && ticket.stage === TicketStageType.Sign) {
    const approvals = judgment.approvalIds.map((id) => ticket.approvals[id]);
    assigneeIds = approvals.map((a) => a.userId);
  }

  return { ticket, judgment, assigneeIds };
};

export default connect(mapStateToProps, { ticketParticipantsSet })(
  withUsers(TicketUserItem),
);
